import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { messageType } from "../../../../typings/interfaces";
import { PageRoute } from "../../../../models";
import { sendBatchMessages, sendMessage } from "../../../../routes/Chat/chat.ducks";
import { CHAT_FILE_TYPE_DOCUMENT } from "../constants";
import { api, notification } from "../../../../services";


interface Options {
  initialContactInfoValues?: { email: string; phoneNumber: string }; // used for initial sending of contact info
  customMessage?: string;// used for sending message using app features (for example send address)
}

export const useTextFormSubmit = (
  adId,
  chatId,
  message,
  setMessage,
  selectedFiles,
  setSelectedFiles,
  textAreaRef
) => {
  const [isUploading, setIsUploading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  const textFormSubmit = async (options?: Options) => {

    const customMessage = options && options.customMessage
    const initialContactInfoValues = options && options.initialContactInfoValues
    const hasMessage = customMessage || message.trim();
    const hasFiles = selectedFiles.length > 0;

    if (!customMessage && !hasMessage && !hasFiles) {
      return;
    }

    setIsUploading(true);

    try {
      // First, send text message if there is one
      if (hasMessage) {
        await new Promise((resolve, reject) => {
          dispatch(sendMessage({
            chatId,
            adId,
            content: customMessage || message.trim(),
            type: messageType.Text,
            contactInfo: initialContactInfoValues ? {
              email: initialContactInfoValues&& initialContactInfoValues.email,
              phoneNumber: initialContactInfoValues && initialContactInfoValues.phoneNumber,
            } : undefined

          }, (response, error) => {
            if (error) {
              reject(error);
            } else {
              if (response.chatId && !chatId) {
                history.push(`${PageRoute.chats}?chatId=${response.chatId}`);
              }
              resolve(response);
            }
          }));
        });
        setMessage("");
      }

      // Then, upload and send files
      if (hasFiles) {
        // Sort files by type: documents first, then images
        const sortedFiles = [...selectedFiles].sort((a, b) => {
          if (a.type === b.type) return 0;
          return a.type === CHAT_FILE_TYPE_DOCUMENT ? -1 : 1;
        });

        // Set all files to uploading status at the start
        setSelectedFiles(prev => prev.map(f => ({ ...f, status: 'uploading' })));

        // Upload all files concurrently
        const uploadPromises = sortedFiles.map(async (fileToUpload) => {
          try {
            // Upload single file
            const [uploadedFile] = await api.Chat.uploadDocuments([fileToUpload.file]);

            // Update status to done
            setSelectedFiles(prev => prev.map(f =>
              f.id === fileToUpload.id ? { ...f, status: 'done' } : f
            ));

            return {
              uploadedFile,
              originalFile: fileToUpload
            };
          } catch (error) {
            // Update status to error
            setSelectedFiles(prev => prev.map(f =>
              f.id === fileToUpload.id ? { ...f, status: 'error' } : f
            ));
            throw error;
          }
        });

        // Wait for all uploads to complete
        const uploadedFiles = await Promise.all(uploadPromises);

        // After all uploads complete, prepare batch messages data
        const messagesData = uploadedFiles.map(({ uploadedFile, originalFile }) => ({
          chatId,
          adId,
          content: uploadedFile.name,
          type: uploadedFile.type,
          fileName: originalFile.originalFileName || originalFile.file.name
        }));

        // Send all messages as a batch to update UI only once
        await new Promise((resolve, reject) => {
          dispatch(sendBatchMessages(messagesData, (response, error) => {
            if (error) {
              reject(error);
            } else {
              if (response.chatId && !chatId) {
                history.push(`${PageRoute.chats}?chatId=${response.chatId}`);
              }
              resolve(response);
            }
          }));
        });

        setSelectedFiles([]);
      }

      setTimeout(() => {
        textAreaRef.current.resizableTextArea.textArea.focus();
      }, 0);

    } catch (error) {
      console.log(error)
      notification.showError("Greška pri slanju poruke");
    } finally {
      setIsUploading(false);
    }
  };

  return { textFormSubmit, isUploading }
}


// WEBPACK FOOTER //
// ./src/containers/ChatsPage/SingleChat/hooks/useTextFormSubmit.ts